import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useSearchParams } from "react-router-dom";
import CONFIG from "utils/config";

const APS_SESSION_CACHE_KEY = "apsSession";
const APS_AUTH_URL = "https://developer.api.autodesk.com/authentication/v2";
const APS_CLIENT_ID = CONFIG.APS_CLIENT_ID;
export const APS_SCOPES = ['data:read', 'data:write', 'data:create', 'bucket:read', 'bucket:create', 'viewables:read', 'account:read'].join(' ');
const SCOPE = encodeURIComponent(APS_SCOPES);

interface ApsSession {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface Hub {
  type: 'hubs';
  id: string;
  attributes: { name: string };
}

interface Project {
  type: 'projects';
  id: string;
  attributes: { name: string };
}

interface Folder {
  type: 'folders';
  id: string;
  attributes: { name: string; displayName: string };
}

interface Item {
  type: 'items';
  id: string;
  attributes: { displayName: string };
}

interface Version {
  type: 'versions';
  id: string;
  attributes: { name: string; versionNumber: number };
  relationships: {
    derivatives?: { data: { id: string } };
  };
}

interface ApsContextType {
  session: ApsSession | null | undefined;
  loading: boolean;
  triggerApsSignup: () => void;
  closeSession: () => void;
  getHubs: () => Promise<Hub[]>;
  getProjects: (hubId: string) => Promise<Project[]>;
  getTopLevelFolders: (hubId: string, projectId: string) => Promise<{ data: Folder[] }>;
  getFolderContents: (projectId: string, folderId: string) => Promise<{ data: (Item | Folder)[] }>;
  getItemVersions: (projectId: string, itemId: string) => Promise<{ data: Version[] }>;
  ensureModelTranslated: (urn: string) => Promise<any>;
}

const ApsContext = createContext<ApsContextType | undefined>(undefined);

export const ApsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const initialized = useRef(false);
  const [refreshSessionTimeout, setRefreshSessionTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [session, setSession] = useState<ApsSession | null | undefined>();
  const [loading, setLoading] = useState(true);
  const { authApi } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const triggerApsSignup = async () => {
    // First check if we have cached credentials
    try {
      const cachedSession = localStorage.getItem(APS_SESSION_CACHE_KEY);
      if (cachedSession) {
        const parsedSession: ApsSession = JSON.parse(cachedSession);
        if (parsedSession.refreshToken) {
          console.log('Found cached APS session, attempting to refresh');
          // Try to refresh the token instead of redirecting
          await refreshSession(parsedSession.refreshToken);
          return; // Exit early if we successfully refreshed the token
        }
      }
    } catch (e) {
      console.error('Error checking cached APS session:', e);
      // Continue with redirect if there was an error checking cache
    }
    
    // If no cached credentials or refresh failed, proceed with redirect
    const response_type = "code";
    const redirect_uri = encodeURIComponent(
      `${window.location.origin}/oauth/aps/callback`
    );
    const originalUrl = encodeURIComponent(window.location.href);
    const authUrl = `${APS_AUTH_URL}/authorize?client_id=${APS_CLIENT_ID}&response_type=${response_type}&redirect_uri=${redirect_uri}&scope=${SCOPE}&state=${originalUrl}`;
    
    console.log("Triggering APS signup", authUrl);
    
    window.location.href = authUrl;
  };

  const refreshSession = async (refreshToken: string) => {
    try {
      if (refreshSessionTimeout) {
        // Clear the previous timeout if it exists
        // This prevents multiple timeouts from being set
        clearTimeout(refreshSessionTimeout);
      }
      const { data } = await authApi.post<ApsSession>(
        `/aps/auth/refresh?refreshToken=${refreshToken}`
      );
      console.log("Refreshed session", data);
      // Set the new session data
      // This will also set the session in local storage
      setSession(data);
      // Set a timeout to refresh the session before it expires
      setRefreshSessionTimeout(
        setTimeout(
          () => {
            refreshSession(data.refreshToken);
          },
          (data.expiresIn - 60) * 1000
        )
      );
    } catch {
      console.log("Failed to refresh session");
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async (authCode: string, redirect: string) => {
    try {
      console.log("=== Creating APS Session ===");
      console.log("Auth code:", authCode);
      console.log("Redirect URL:", redirect);
      
      const redirect_uri = encodeURIComponent(
        `${window.location.origin}/oauth/aps/callback`
      );
      
      console.log("Calling /aps/auth/exchange...");
      const { data } = await authApi.post<ApsSession>(
        `/aps/auth/exchange?authCode=${authCode}&redirectUri=${redirect_uri}`
      );
      console.log("✅ Session created successfully:", data);
      
      // Manually save to localStorage before redirecting
      // This ensures the session persists across the page reload
      console.log("Saving to localStorage with key:", APS_SESSION_CACHE_KEY);
      localStorage.setItem(APS_SESSION_CACHE_KEY, JSON.stringify(data));
      
      // Verify it was saved
      const saved = localStorage.getItem(APS_SESSION_CACHE_KEY);
      console.log("✅ Verified localStorage save:", saved ? "SUCCESS" : "FAILED");
      
      // Set the session state
      setSession(data);
      console.log("✅ Session state updated");
      
      // Set a timeout to refresh the session before it expires
      setRefreshSessionTimeout(
        setTimeout(
          () => {
            refreshSession(data.refreshToken);
          },
          (data.expiresIn - 60) * 1000
        )
      );
      console.log("✅ Refresh timeout set for", (data.expiresIn - 60), "seconds");
      
      // Small delay to ensure localStorage write completes
      await new Promise(resolve => setTimeout(resolve, 100));
      
      console.log("🔄 Redirecting to:", redirect);
      window.location.replace(redirect);
    } catch (error) {
      console.error("❌ Failed to create session:", error);
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  const closeSession = async () => {
    try {
      console.log("Closing session");
      setSession(null);
    } catch (e) {
      console.error("Error closing session", e);
    }
  };

  const getHubs = async (): Promise<Hub[]> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post<Hub[]>("/aps/get-hubs", {
        accessToken: session.accessToken,
      });
      return data;
    } catch (error) {
      console.error("Error fetching hubs:", error);
      throw error;
    }
  };

  const getProjects = async (hubId: string): Promise<Project[]> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post<Project[]>("/aps/get-projects", {
        accessToken: session.accessToken,
        hubId,
      });
      return data;
    } catch (error) {
      console.error("Error fetching projects:", error);
      throw error;
    }
  };

  const getTopLevelFolders = async (
    hubId: string,
    projectId: string
  ): Promise<{ data: Folder[] }> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post<{ data: Folder[] }>(
        "/aps/get-top-level-folders-of-a-project",
        {
          accessToken: session.accessToken,
          hubId,
          projectId,
        }
      );
      return data;
    } catch (error) {
      console.error("Error fetching top-level folders:", error);
      throw error;
    }
  };

  const getFolderContents = async (
    projectId: string,
    folderId: string
  ): Promise<{ data: (Item | Folder)[] }> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post<{ data: (Item | Folder)[] }>(
        "/aps/get-folder-contents",
        {
          accessToken: session.accessToken,
          projectId,
          folderId,
        }
      );
      return data;
    } catch (error) {
      console.error("Error fetching folder contents:", error);
      throw error;
    }
  };

  const getItemVersions = async (
    projectId: string,
    itemId: string
  ): Promise<{ data: Version[] }> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post<{ data: Version[] }>(
        "/aps/get-item-versions",
        {
          accessToken: session.accessToken,
          projectId,
          itemId,
        }
      );
      return data;
    } catch (error) {
      console.error("Error fetching item versions:", error);
      throw error;
    }
  };

  const ensureModelTranslated = async (urn: string): Promise<any> => {
    if (!session?.accessToken) {
      throw new Error("No active session");
    }
    try {
      const { data } = await authApi.post("/aps/ensure-model-translated", {
        accessToken: session.accessToken,
        urn,
      });
      return data;
    } catch (error) {
      console.error("Error checking model translation:", error);
      throw error;
    }
  };

  const loadCachedSession = async () => {
    try {
      console.log("=== Loading Cached APS Session ===");
      console.log("Looking for key:", APS_SESSION_CACHE_KEY);
      
      const cachedSession = localStorage.getItem(APS_SESSION_CACHE_KEY);
      console.log("Cached session found:", cachedSession ? "YES" : "NO");
      
      if (!cachedSession) {
        throw new Error("No cached session found");
      }
      
      const parsedSession: ApsSession = JSON.parse(cachedSession);
      console.log("Parsed session:", parsedSession);
      
      if (!parsedSession.refreshToken) {
        throw new Error("No refresh token found");
      }
      
      console.log("Refreshing session with cached token...");
      // Try using the refresh token
      await refreshSession(parsedSession.refreshToken);
      console.log("✅ Session loaded and refreshed successfully");
    } catch (e) {
      console.error("❌ Failed to load cached session:", e);
      // If the cached session is invalid, clear it
      setSession(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Save Session to local storage on save
    console.log("Saving session to local storage", session);
    if (session) {
      localStorage.setItem(APS_SESSION_CACHE_KEY, JSON.stringify(session));
    } else if (session === null) {
      // If session is null, remove it from local storage
      localStorage.removeItem(APS_SESSION_CACHE_KEY);
    }
  }, [session]);

  useEffect(() => {
    if (initialized.current) {
      console.log("ApsContext already initialized, skipping");
      return;
    }
    initialized.current = true;
    
    // If code is in params, try to create a new session  
    const authCode = searchParams.get("code");
    const state = searchParams.get("state");  
    const shouldInitializeSession = window.location.pathname === "/oauth/aps/callback";
    
    console.log("=== APS Context Initialization ===");
    console.log("shouldInitializeSession:", shouldInitializeSession);
    console.log("window.location.pathname:", window.location.pathname);
    console.log("authCode:", authCode);
    console.log("state:", state);

    if (shouldInitializeSession && authCode && state) {
      console.log("Creating new session from OAuth callback");
      // Create a new session
      createSession(authCode, state);
    } else {
      console.log("Loading cached session");
      // Try to load the cached session
      loadCachedSession();
    }
  }, []);

  return (
    <ApsContext.Provider
      value={{
        session,
        loading,
        triggerApsSignup,
        closeSession,
        getHubs,
        getProjects,
        getTopLevelFolders,
        getFolderContents,
        getItemVersions,
        ensureModelTranslated,
      }}
    >
      {children}
    </ApsContext.Provider>
  );
};

export const useAps = (): ApsContextType => {
  const context = useContext(ApsContext);
  if (!context) {
    throw new Error("useAps must be used within an ApsProvider");
  }
  return context;
};
